
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useBuyer } from "@/contexts/BuyerContext";

const buyerInvestmentPreferencesSchema = z.object({
  purchasingType: z.array(z.string()).min(1, "Please select at least one purchasing type"),
  averageCloseTime: z.string().optional(),
  preferredMarkets: z.array(z.string()).min(1, "Please select at least one market"),
  emailNotificationsEnabled: z.boolean(),
  notificationFrequency: z.enum(["instant", "daily", "weekly"]),
});

export type BuyerInvestmentPreferencesForm = z.infer<typeof buyerInvestmentPreferencesSchema>;

export const useBuyerInvestmentPreferences = () => {
  const { buyerProfile, updateBuyerProfile, isLoading } = useBuyer();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [selectedPurchasingTypes, setSelectedPurchasingTypes] = useState<string[]>([]);

  const form = useForm<BuyerInvestmentPreferencesForm>({
    resolver: zodResolver(buyerInvestmentPreferencesSchema),
    defaultValues: {
      purchasingType: [],
      averageCloseTime: "",
      preferredMarkets: [],
      emailNotificationsEnabled: true,
      notificationFrequency: "instant" as const,
    },
  });

  // Update form values when buyer profile loads
  useEffect(() => {
    if (buyerProfile) {
      const markets = Array.isArray(buyerProfile.preferred_markets) ? buyerProfile.preferred_markets : [];
      const purchasingTypes = Array.isArray(buyerProfile.purchasing_type) ? buyerProfile.purchasing_type : [];
      
      setSelectedMarkets(markets);
      setSelectedPurchasingTypes(purchasingTypes);
      
      form.reset({
        purchasingType: purchasingTypes,
        averageCloseTime: buyerProfile.average_close_time || "",
        preferredMarkets: markets,
        emailNotificationsEnabled: buyerProfile.email_notifications_enabled ?? true,
        notificationFrequency: (buyerProfile.notification_frequency as "instant" | "daily" | "weekly") || "instant",
      });
    }
  }, [buyerProfile, form]);

  const handleMarketToggle = (market: string, checked: boolean) => {
    let newMarkets;
    if (checked) {
      newMarkets = [...selectedMarkets, market];
    } else {
      newMarkets = selectedMarkets.filter(m => m !== market);
    }
    setSelectedMarkets(newMarkets);
    form.setValue("preferredMarkets", newMarkets);
  };

  const handlePurchasingTypeToggle = (type: string, checked: boolean) => {
    let newTypes;
    if (checked) {
      newTypes = [...selectedPurchasingTypes, type];
    } else {
      newTypes = selectedPurchasingTypes.filter(t => t !== type);
    }
    setSelectedPurchasingTypes(newTypes);
    form.setValue("purchasingType", newTypes);
  };

  const onSubmit = async (data: BuyerInvestmentPreferencesForm) => {
    try {
      await updateBuyerProfile({
        purchasing_type: data.purchasingType,
        average_close_time: data.averageCloseTime || null,
        preferred_markets: data.preferredMarkets,
        email_notifications_enabled: data.emailNotificationsEnabled,
        notification_frequency: data.notificationFrequency,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating investment preferences:", error);
    }
  };

  const handleCancel = () => {
    if (buyerProfile) {
      const markets = Array.isArray(buyerProfile.preferred_markets) ? buyerProfile.preferred_markets : [];
      const purchasingTypes = Array.isArray(buyerProfile.purchasing_type) ? buyerProfile.purchasing_type : [];
      
      setSelectedMarkets(markets);
      setSelectedPurchasingTypes(purchasingTypes);
      
      form.reset({
        purchasingType: purchasingTypes,
        averageCloseTime: buyerProfile.average_close_time || "",
        preferredMarkets: markets,
        emailNotificationsEnabled: buyerProfile.email_notifications_enabled ?? true,
        notificationFrequency: (buyerProfile.notification_frequency as "instant" | "daily" | "weekly") || "instant",
      });
    }
    setIsEditing(false);
  };

  return {
    form,
    isEditing,
    setIsEditing,
    selectedMarkets,
    selectedPurchasingTypes,
    isLoading,
    handleMarketToggle,
    handlePurchasingTypeToggle,
    onSubmit,
    handleCancel
  };
};
